/* eslint-disable */
import React, { useState } from 'react';
import {
  TextField,
  Checkbox,
  InputLabel,
  Box,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import { ReactComponent as Cancel } from '../../Assets/images/svg/dar_dark_cancel.svg';
import './multiSelectAutocomplete.scss';

// Virtualized Listbox for performance
const LISTBOX_PADDING = 8;
function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}
const NoScrollOuter = React.forwardRef((props, ref) => (
  <div
    {...props}
    ref={ref}
    style={{
      ...props.style,
      overflowY: 'auto',
      overflowX: 'hidden',
    }}
  />
));

const NoScrollInner = React.forwardRef((props, ref) => (
  <div
    {...props}
    ref={ref}
    style={{
      ...props.style,
      width: '100%',
    }}
  />
));
const VirtualizedListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  return (
    <div ref={ref} {...other} className="custom-listbox">
      <FixedSizeList
        outerElementType={NoScrollOuter}
        innerElementType={NoScrollInner}
        height={Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING}
        width="100%"
        itemSize={itemSize}
        itemCount={itemCount}
        itemData={itemData}
        overscanCount={5}
      >
        {renderRow}
      </FixedSizeList>
    </div>
  );
});

// Component
const MultiSelectAutocomplete = ({
  label,
  options = [],
  onChange,
  placeholder,
  value,
  multiSelectCss,
  disabled,
  freeSolo = false,
  isOnBlur,
  isFilterRequired = true,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleBlur = () => {
    if (isOnBlur && inputValue.trim()) {
      const newValue = [...value, inputValue.trim()];
      const processedValue = newValue.map((item) =>
        typeof item === 'string' ? { id: item, name: item } : item
      );
      onChange(null, processedValue);
      setInputValue('');
    }
  };
  const filter = createFilterOptions({
    stringify: (option) =>
      typeof option === 'string' ? option : option.name || '',
  });

  return (
    <Box className={`common-autocomplete-root ${multiSelectCss}`}>
      <InputLabel className="autocomplete-input-label">{label}</InputLabel>
      <Autocomplete
        multiple
        freeSolo={options.length === 0 || freeSolo}
        disableCloseOnSelect
        options={options}
        value={value}
        inputValue={inputValue}
        ListboxComponent={VirtualizedListboxComponent}
        filterOptions={(opts, params) => {
          if (isFilterRequired) {
            return filter(opts, params);
          }
          return createFilterOptions({
            stringify: (option) =>
              typeof option === 'string' ? option : option.name || '',
          })(opts, params);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onBlur={handleBlur}
        onChange={(event, newValue) => {
          const processedValue = newValue.map((item) =>
            typeof item === 'string' ? { id: item, name: item } : item
          );
          onChange(event, processedValue);
          setInputValue('');
        }}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.name || ''
        }
        disabled={disabled}
        className="common-autocomplete"
        ChipProps={{
          deleteIcon: <Cancel />,
          classes: {
            deleteIcon: 'autocomplete-tag-delete-icon',
          },
        }}
        renderOption={(props, option, { selected }) => {
          if (options.length === 0) return null;
          const { ...optionProps } = props;
          return (
            <li
              key={option.id || option.name}
              {...optionProps}
              className="autocomplete-menu-item"
            >
              <Checkbox
                className="autocomplete-checkbox"
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                checked={selected}
              />
              {option.name || option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!value?.length ? placeholder : ''}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: <>{params.InputProps.startAdornment}</>,
            }}
          />
        )}
      />
    </Box>
  );
};

MultiSelectAutocomplete.propTypes = {
  options: PropTypes.array,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.array,
  multiSelectCss: PropTypes.string,
  disabled: PropTypes.bool,
  freeSolo: PropTypes.bool,
  isOnBlur: PropTypes.bool,
  isFilterRequired: PropTypes.bool,
};

VirtualizedListboxComponent.propTypes = {
  children: PropTypes.node,
};

export default MultiSelectAutocomplete;
